import { niceStep } from 'components/Widget/shared/axis';
import { formatCompactCurrency, formatCurrency } from 'components/Widget/shared/currency';
import { cn } from 'utils/styles';
import {
  CHART_COPY,
  CHART_STROKES,
  CHART_TICK_COUNT,
  formatDuration,
  MONTHLY_TICK_MAX_MONTHS,
  MONTHS_PER_YEAR,
  TICK_MONTHS_MIN_GAP,
  TICK_MONTHS_WHEN_SPARSE,
  TICK_YEARS_WHEN_SPARSE,
  YEARLY_TICK_MAX_MONTHS,
} from './constants';
import type { LoanBalancePoint, LoanResult } from './types';

/** A unitless coordinate space stretched to the container; `non-scaling-stroke` keeps the line even. */
const VIEW_WIDTH = 600;

const VIEW_HEIGHT = 200;

const STROKE_WIDTH = 2;

const PLOT_TOP = STROKE_WIDTH;

const PLOT_HEIGHT = VIEW_HEIGHT - STROKE_WIDTH * 2;

export const ZERO_BASELINE_Y = PLOT_TOP + PLOT_HEIGHT;

interface AxisTick {
  elapsedMonths: number;
  label: string;
}

/**
 * Floors rather than rounds up, because a tick past the payoff has nowhere on the axis to sit: the
 * label would be clamped to the right edge and read as the payoff itself, contradicting the pill
 * above it. Years are left to land where they fall - a term of 18 months ends on an unlabelled
 * edge rather than growing a fractional `1.5 yr` tick.
 */
const yearAxisTicks = (monthsToPayoff: number): AxisTick[] => {
  const step = monthsToPayoff <= YEARLY_TICK_MAX_MONTHS ? 1 : TICK_YEARS_WHEN_SPARSE;
  const ticks: AxisTick[] = [];

  for (let year = step; year <= Math.floor(monthsToPayoff / MONTHS_PER_YEAR); year += step) {
    ticks.push({ elapsedMonths: year * MONTHS_PER_YEAR, label: CHART_COPY.yearTick(year) });
  }

  return ticks;
};

/**
 * Ends on the payoff rather than on the last whole quarter, because the shortest terms clear no
 * quarter at all and an axis with no labels reads as a chart that failed to render. A quarterly
 * tick within `TICK_MONTHS_MIN_GAP` of the payoff gives way instead of overlapping it.
 */
const monthAxisTicks = (monthsToPayoff: number): AxisTick[] => {
  const ticks: AxisTick[] = [];

  for (let month = TICK_MONTHS_WHEN_SPARSE; month <= monthsToPayoff; month += TICK_MONTHS_WHEN_SPARSE) {
    if (monthsToPayoff - month > TICK_MONTHS_MIN_GAP) {
      ticks.push({ elapsedMonths: month, label: CHART_COPY.monthTick(month) });
    }
  }

  ticks.push({ elapsedMonths: monthsToPayoff, label: CHART_COPY.monthTick(monthsToPayoff) });

  return ticks;
};

const axisTicks = (monthsToPayoff: number): AxisTick[] =>
  monthsToPayoff <= MONTHLY_TICK_MAX_MONTHS ? monthAxisTicks(monthsToPayoff) : yearAxisTicks(monthsToPayoff);

interface PolylineProps {
  points: readonly LoanBalancePoint[];
  xMaxMonths: number;
  yMax: number;
  stroke: (typeof CHART_STROKES)[keyof typeof CHART_STROKES];
}

/**
 * Both lines share the baseline's axis, so the shorter schedule stops partway across rather than
 * being restretched to the full width - which is what makes the months saved visible as a gap.
 */
const BalanceLine = ({ points, xMaxMonths, yMax, stroke }: PolylineProps) => (
  <polyline
    points={points
      .map(point => {
        const x = (point.elapsedMonths / xMaxMonths) * VIEW_WIDTH;
        const y = PLOT_TOP + PLOT_HEIGHT - (point.remainingBalance / yMax) * PLOT_HEIGHT;

        return `${x},${y}`;
      })
      .join(' ')}
    fill="none"
    strokeWidth={STROKE_WIDTH}
    strokeDasharray={stroke.dash}
    strokeLinejoin="round"
    strokeLinecap="round"
    className={stroke.className}
    vectorEffect="non-scaling-stroke"
  />
);

interface LoanBalanceLineGraphProps {
  result: LoanResult;
  showComparison: boolean;
}

export const LoanBalanceLineGraph = ({ result, showComparison }: LoanBalanceLineGraphProps) => {
  const { baseline, withExtra } = result;
  const openingBalance = baseline.balanceSeries[0]?.remainingBalance ?? 0;
  const step = niceStep(openingBalance / CHART_TICK_COUNT);
  const yMax = step * CHART_TICK_COUNT;
  const ticks = axisTicks(baseline.monthsToPayoff);
  const label = showComparison
    ? CHART_COPY.comparisonLabel(
        formatDuration(baseline.monthsToPayoff),
        formatCurrency(openingBalance),
        formatDuration(withExtra.monthsToPayoff),
      )
    : CHART_COPY.label(formatDuration(baseline.monthsToPayoff), formatCurrency(openingBalance));

  return (
    <div className="flex flex-col gap-2">
      <p className="text-body-saans text-content-secondary">{CHART_COPY.title}</p>

      {/* The plot and its gridlines share this box, so a line reaches the same right edge they do. */}
      <div className="relative ml-12 h-48">
        <div aria-hidden className="absolute inset-0">
          {Array.from({ length: CHART_TICK_COUNT + 1 }, (_, index) => (
            <div
              key={index}
              style={{ bottom: `${(index / CHART_TICK_COUNT) * 100}%` }}
              className="absolute inset-x-0 flex translate-y-1/2 items-center">
              <span className="absolute right-full w-10 pr-2 text-right text-legal text-content-secondary">
                {formatCompactCurrency(step * index)}
              </span>
              <span className="h-px w-full bg-border-primary" />
            </div>
          ))}
        </div>

        <svg
          role="img"
          aria-label={label}
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          className="relative h-full w-full">
          <BalanceLine
            points={baseline.balanceSeries}
            xMaxMonths={baseline.monthsToPayoff}
            yMax={yMax}
            stroke={CHART_STROKES.baseline}
          />
          {showComparison && (
            <BalanceLine
              points={withExtra.balanceSeries}
              xMaxMonths={baseline.monthsToPayoff}
              yMax={yMax}
              stroke={CHART_STROKES.withExtra}
            />
          )}
        </svg>
      </div>

      <div aria-hidden className="relative ml-12 h-4 text-legal text-content-secondary">
        {ticks.map(({ elapsedMonths, label: tickLabel }) => {
          const offsetPct = (elapsedMonths / baseline.monthsToPayoff) * 100;
          // A centred label at the payoff would overhang the plot and wrap; the last one ends there instead.
          const isLast = offsetPct >= 100;

          return (
            <span
              key={elapsedMonths}
              style={isLast ? { right: 0 } : { left: `${offsetPct}%` }}
              className={cn('absolute whitespace-nowrap', !isLast && '-translate-x-1/2')}>
              {tickLabel}
            </span>
          );
        })}
      </div>

      {/*
        Only drawn alongside the second line: a key naming a series the chart is not plotting would
        describe something the reader cannot see. The swatches repeat the dash pattern rather than
        colour alone, for the same reason the lines carry one.
      */}
      {showComparison && (
        <ul className="mt-1 flex flex-wrap gap-x-6 gap-y-2 text-legal text-content-secondary">
          {CHART_COPY.legend.map(({ key, label: seriesLabel }) => (
            <li key={key} className="flex items-center gap-2">
              <svg aria-hidden viewBox="0 0 24 2" className="h-0.5 w-6 overflow-visible">
                <line
                  x1="0"
                  y1="1"
                  x2="24"
                  y2="1"
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={CHART_STROKES[key].dash}
                  strokeLinecap="round"
                  className={CHART_STROKES[key].className}
                />
              </svg>
              {seriesLabel}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
